import {zodResolver} from "@hookform/resolvers/zod";
import {IBAN_INVALID_MESSAGE, type ICompany, IbanSchema, formatIban} from "@mytour/shared";
import {FloatingField} from "@mytour/ui/form";
import {isAxiosError} from "axios";
import {useEffect} from "react";
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {Button, Form, FormGroup, Input, Label, Spinner} from "reactstrap";
import {z} from "zod";
import {useUpdateInvoiceParameters} from "../../../query/companySettingsQueries";

type Props = {
  company: ICompany;
};

const invoiceParametersSchema = z.object({
  iban: z.string(),
  deadlineToPay: z.string(),
  latePayment: z.string(),
  invoiceFirstNumber: z.string(),
  invoicePrefix: z.string(),
});

type InvoiceParametersFormData = z.infer<typeof invoiceParametersSchema>;

export function InvoiceParametersSubTab({company}: Props) {
  const {t: translate} = useTranslation();
  const {updateInvoiceParameters, isUpdatingInvoiceParameters} = useUpdateInvoiceParameters();
  const disabled = isUpdatingInvoiceParameters;

  const existingIban = company.invoicing.iban ?? "";
  const displayedIban = formatIban(existingIban);
  const schema = invoiceParametersSchema.extend({
    iban: z.union([z.literal(displayedIban).transform(() => existingIban), IbanSchema], {
      errorMap: () => ({message: IBAN_INVALID_MESSAGE}),
    }),
  });
  const {
    register,
    handleSubmit,
    control,
    setError,
    setFocus,
    formState: {errors},
  } = useForm<InvoiceParametersFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      iban: displayedIban,
      deadlineToPay: company.invoicing.deadlineToPay?.toString() ?? "",
      latePayment: company.invoicing.latePayment ?? "",
      invoiceFirstNumber: company.invoicing.invoiceFirstNumber?.toString() ?? "",
      invoicePrefix: company.invoicing.invoicePrefix ?? "",
    },
  });

  useEffect(() => {
    if (!disabled && errors.iban?.type === "server") {
      setFocus("iban");
    }
  }, [disabled, errors.iban?.type, setFocus]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <h5 className="mt-4">Numérotation des factures</h5>

      <FormGroup>
        <Label>Numéro de la première facture</Label>
        <Controller
          name="invoiceFirstNumber"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              value={field.value ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                field.onChange(val === "" ? "" : val);
              }}
              type="number"
              id="invoiceFirstNumber"
              step="1"
              min="1"
            />
          )}
        />
      </FormGroup>

      <FormGroup>
        <Label for="invoicePrefix">{translate("myTourApp.platformUser.invoicePrefix")}</Label>
        <Input
          id="invoicePrefix"
          type="text"
          innerRef={register("invoicePrefix").ref}
          onChange={register("invoicePrefix").onChange}
          onBlur={register("invoicePrefix").onBlur}
          name="invoicePrefix"
          disabled={disabled}
        />
      </FormGroup>

      <h5>Coordonnées bancaires</h5>

      <FloatingField
        name="iban"
        label={translate("myTourApp.platformUser.iban")}
        control={control}
        error={errors.iban}
        formatOnBlur={formatIban}
        disabled={disabled}
      />

      <h5 className="mt-4">Conditions de paiement</h5>

      <FormGroup>
        <Label>Délai de règlement (en jours)</Label>
        <Controller
          name="deadlineToPay"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              value={field.value ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                field.onChange(val === "" ? "" : val);
              }}
              type="number"
              id="deadlineToPay"
              step="1"
              min="0"
            />
          )}
        />
      </FormGroup>

      <FormGroup>
        <Label for="latePayment">{translate("myTourApp.platformUser.latePayment")}</Label>
        <Input
          type="textarea"
          id="latePayment"
          innerRef={register("latePayment").ref}
          onChange={register("latePayment").onChange}
          onBlur={register("latePayment").onBlur}
          name="latePayment"
        />
      </FormGroup>

      <Button color="primary" type="submit" disabled={disabled}>
        {isUpdatingInvoiceParameters ? <Spinner size="sm" /> : null}
        {isUpdatingInvoiceParameters ? " " : ""}Sauvegarder
      </Button>
    </Form>
  );

  async function onSubmit(data: InvoiceParametersFormData) {
    try {
      await updateInvoiceParameters({
        ...(data.iban === existingIban ? {} : {iban: data.iban}),
        deadlineToPay: data.deadlineToPay ? Number(data.deadlineToPay) : undefined,
        latePayment: data.latePayment,
        invoiceFirstNumber: data.invoiceFirstNumber ? Number(data.invoiceFirstNumber) : undefined,
        invoicePrefix: data.invoicePrefix,
      });
      toast.success("Paramètres de facturation enregistrés");
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data?.message === "Validation error" &&
        Array.isArray(error.response.data?.issues) &&
        error.response.data.issues.some(
          (issue: {path?: unknown} | null) =>
            Array.isArray(issue?.path) && issue.path.length === 1 && issue.path[0] === "iban"
        )
      ) {
        setError("iban", {type: "server", message: IBAN_INVALID_MESSAGE});
        return;
      }
      toast.error("Erreur lors de la sauvegarde des paramètres");
    }
  }
}
